import { Info } from "lucide-react";
import { useParams } from "react-router";
import { useUsage } from "../api/queries";
import { useT } from "../i18n/core";
import { useFormat } from "../lib/format";
import { ErrorState, LoadingRows, PageHead, Panel } from "../ui/Common";
import { Meter } from "../ui/Meter";
import { UsageChart } from "../ui/UsageChart";
import { usageMessages } from "./usage.i18n";

export function UsagePage() {
  const { orgId = "" } = useParams();
  const u = useUsage(orgId);
  const t = useT(usageMessages);
  const f = useFormat();
  if (u.isPending) return <><PageHead title={t.title} /><div className="panel"><LoadingRows rows={8} /></div></>;
  if (u.isError) return <ErrorState error={u.error} />;
  const d = u.data;
  // A franquia é o espaço ocupado (ADR 0037); quota zero é sem limite.
  const share = d.quotaBytes > 0 ? d.storedBytes / d.quotaBytes : null;
  const top = d.topMachines[0]?.protectedBytes ?? 1;

  return (
    <>
      <PageHead
        title={t.title}
        lede={t.lede(d.planName, f.dayMonth(d.periodStart), f.dayMonth(d.periodEnd))}
      />

      <Panel title={t.protectedTitle} sub={t.protectedSub}>
        <div style={{ display: "grid", gap: 16 }}>
          <div className="protection-figure">
            <span className="big num">{f.gb(d.storedBytes)}</span>
            <span className="of">{share === null ? t.noLimit : t.ofQuota(f.gb(d.quotaBytes), f.pct(share))}</span>
          </div>
          {share !== null && <Meter value={d.storedBytes} max={d.quotaBytes} />}
          <div className="stats" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
            {share !== null && <div className="stat"><span className="v">{f.gb(Math.max(0, d.quotaBytes - d.storedBytes))}</span><span className="k">{t.available}</span></div>}
            <div className="stat"><span className="v">{f.gb(d.protectedBytes)}</span><span className="k">{t.protectedAtSource}</span></div>
            <div className="stat"><span className="v">{d.storedBytes > 0 ? f.ratio(d.protectedBytes / d.storedBytes) : "—"}</span><span className="k">{t.savings}</span></div>
          </div>
          <div className="callout">
            <Info aria-hidden="true" />
            <span>
              {d.overage === "charge" ? t.overageCharge : t.overageBlock} {t.gbDefinition(f.int(1_000_000_000))} {t.storedNote}
            </span>
          </div>
        </div>
      </Panel>

      <Panel title={t.last30}>
        <UsageChart daily={d.daily} quota={d.quotaBytes} />
      </Panel>

      <Panel title={t.topMachines} sub={t.topMachinesSub} bodyless>
        <div className="table-wrap">
          <table className="data">
            <thead><tr><th>{t.colMachine}</th><th className="right">{t.colProtected}</th><th style={{ width: "40%" }}>{t.colShare}</th></tr></thead>
            <tbody>
              {d.topMachines.map((m) => (
                <tr key={m.machineId}>
                  <td className="mono">{m.hostname}</td>
                  <td className="right num">{f.bytes(m.protectedBytes)}</td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ flex: 1, height: 8, background: "var(--accent-soft)", borderRadius: 99 }}>
                        <div style={{ width: `${(m.protectedBytes / top) * 100}%`, height: "100%", background: "var(--series-1)", borderRadius: 99 }} />
                      </div>
                      <span className="num muted" style={{ width: 40, textAlign: "right" }}>{f.pct(m.protectedBytes / d.protectedBytes)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}
